import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Download, Eye, Loader2 } from "lucide-react";
import { TYPE_LABEL, fmtDate, typeLabel } from "@/components/legal/AgreementBody";
import { downloadAcceptancePdf } from "@/components/legal/downloadPdf";

const STATE_LABEL = {
  accepted: "Current",
  accepted_prior_version: "Accepted earlier version",
  reacceptance_required: "Action required",
  acceptance_required: "Action required",
  not_required: "Not required",
};

function MarketingConsent({ consent, onChange }) {
  const [busy, setBusy] = useState(false);
  const toggle = async () => {
    try {
      setBusy(true);
      const { data } = await api.post("/legal/marketing-consent", { granted: !consent.granted });
      onChange(data.marketing_consent);
      toast.success(data.marketing_consent.granted ? "Marketing emails turned on." : "Marketing consent withdrawn.");
    } catch {
      toast.error("Could not update your preference.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="pf-card p-5 flex flex-wrap items-center justify-between gap-3" data-testid="legal-marketing-card">
      <div>
        <div className="font-semibold text-slate-900">Marketing communications</div>
        <div className="text-xs text-slate-600 mt-0.5" data-testid="legal-marketing-status">
          {consent.granted ? "Subscribed" : "Not subscribed"} · Updated {fmtDate(consent.updated_at)}
        </div>
      </div>
      <button onClick={toggle} disabled={busy} data-testid="legal-marketing-toggle"
              className="pf-btn-glass px-4 py-2 rounded-full text-xs font-semibold">
        {consent.granted ? "Withdraw consent" : "Opt in"}
      </button>
    </div>
  );
}

export default function AgreementsHistory({ showMarketing }) {
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get("/legal/my-agreements").then((r) => setData(r.data)).catch(() => toast.error("Could not load your terms history")), []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <div className="py-12 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  const currentIds = new Set((data.status?.current || []).map((c) => c.agreement_id));

  return (
    <div className="space-y-6" data-testid="legal-agreements-history">
      <div className="pf-card overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="text-left text-xs uppercase tracking-widest text-slate-500">
            <tr><th className="px-5 py-3">Terms</th><th className="px-5 py-3">Terms version</th><th className="px-5 py-3">Accepted</th><th className="px-5 py-3">Status</th><th className="px-5 py-3">Action</th></tr>
          </thead>
          <tbody>
            {data.acceptances.map((a) => {
              const superseded = a.superseded_by_acceptance_id || !currentIds.has(a.agreement_id);
              return (
                <tr key={a.acceptance_id} className="border-t border-slate-100" data-testid={`legal-row-${a.acceptance_id}`}>
                  <td className="px-5 py-3 font-medium text-slate-900">{TYPE_LABEL[a.agreement_type] || a.agreement_title || typeLabel(a.agreement_type)}</td>
                  <td className="px-5 py-3 font-mono text-xs">{a.agreement_version}</td>
                  <td className="px-5 py-3 text-xs">{fmtDate(a.accepted_at)}</td>
                  <td className="px-5 py-3">
                    <span className={`pf-chip ${superseded ? "" : "pf-chip-cyan"}`} data-testid={`legal-row-status-${a.acceptance_id}`}>
                      {superseded ? "Superseded" : "Current"}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-xs font-semibold">
                    <a href={`/terms/documents/${a.agreement_id}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 underline mr-3" data-testid={`legal-row-view-${a.acceptance_id}`}>
                      <Eye className="w-3 h-3" /> View
                    </a>
                    <button onClick={() => downloadAcceptancePdf(a.acceptance_id)} className="inline-flex items-center gap-1 underline" data-testid={`legal-row-pdf-${a.acceptance_id}`}>
                      <Download className="w-3 h-3" /> Download PDF
                    </button>
                  </td>
                </tr>
              );
            })}
            {data.acceptances.length === 0 && (
              <tr><td colSpan={5} className="px-5 py-10 text-center text-slate-500" data-testid="legal-empty">No Terms & Conditions accepted yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {(data.status?.current || []).some((c) => c.state !== "accepted") && (
        <div className="pf-card p-5 text-sm" data-testid="legal-pending-list">
          <div className="font-semibold text-slate-900 mb-2">Current versions</div>
          {(data.status.current || []).map((c) => (
            <div key={c.agreement_id} className="flex justify-between py-1 text-xs">
              <span>{typeLabel(c.agreement_type)} {c.version}</span><span>{STATE_LABEL[c.state] || c.state}</span>
            </div>
          ))}
        </div>
      )}

      {showMarketing && <MarketingConsent consent={data.marketing_consent} onChange={(mc) => setData((d) => ({ ...d, marketing_consent: mc }))} />}
    </div>
  );
}
